import { useEffect, useState } from 'react';

import {
  ChakraBox,
  ChakraFlex,
  PaginationV2,
} from '@equip.health/ui';

import { getDiffInWeeks } from '~/lib/util/patient.util';
import { SEVERITY_ORDER } from '../constants';
import { PatientProviderDashboard } from '~/store/services/providerDashboard';
import PatientRow from './PatientRow';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { ProviderDashboardEvents } from '~/analytics/providerDashboardEvents';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';

const DATE_FORMAT = 'yyyy-MM-dd';
const DEFAULT_PAGE_SIZE = 10;

const TRANSITION_TRACK = 'Transition Track';
const PAUSED_STATUS = 'paused';

const getStatusPriority = (patient: PatientProviderDashboard): number => {
  if (patient.programName === TRANSITION_TRACK) return 0;
  if (patient.patientStatus?.toLowerCase() === PAUSED_STATUS) return 1;
  return 2;
};

const defaultSortFn = (
  a: PatientProviderDashboard,
  b: PatientProviderDashboard,
): number => {
  const severityA = SEVERITY_ORDER.indexOf(a.colorLevel?.toLowerCase());
  const severityB = SEVERITY_ORDER.indexOf(b.colorLevel?.toLowerCase());
  const safeSeverityA = severityA === -1 ? SEVERITY_ORDER.length : severityA;
  const safeSeverityB = severityB === -1 ? SEVERITY_ORDER.length : severityB;

  if (safeSeverityA !== safeSeverityB) return safeSeverityA - safeSeverityB;

  const priorityA = getStatusPriority(a);
  const priorityB = getStatusPriority(b);
  if (priorityA !== priorityB) return priorityA - priorityB;

  const weekA = getDiffInWeeks(a.admitDate, undefined, DATE_FORMAT);
  const weekB = getDiffInWeeks(b.admitDate, undefined, DATE_FORMAT);
  const safeWeekA =
    Number.isFinite(weekA) && weekA > 0 ? weekA : Number.MAX_SAFE_INTEGER;
  const safeWeekB =
    Number.isFinite(weekB) && weekB > 0 ? weekB : Number.MAX_SAFE_INTEGER;

  return safeWeekA - safeWeekB;
};

interface PatientListProps {
  patients: PatientProviderDashboard[];
  pageSize?: number;
  sortFn?: (a: PatientProviderDashboard, b: PatientProviderDashboard) => number;
  preserveOrder?: boolean;
  tableName?: string;
  tabKey?: string;
  rowComponent?: React.ComponentType<{ patient: PatientProviderDashboard }>;
}

const PatientList = ({
  patients,
  pageSize = DEFAULT_PAGE_SIZE,
  sortFn = defaultSortFn,
  preserveOrder = false,
  tableName = 'active-patients',
  tabKey,
  rowComponent: RowComponent = PatientRow,
}: PatientListProps) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(pageSize);
  const { trackEvent } = useAnalytics() ?? {};

  useEffect(() => {
    setCurrentPage(1);
  }, [patients]);

  const sorted = preserveOrder ? patients : [...patients].sort(sortFn);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const pageItems = sorted.slice(startIndex, startIndex + itemsPerPage);

  const handlePageChange = (page: number) => {
    trackEvent?.(ProviderDashboardEvents.myPatientsPageChanged, {
      page,
      itemsPerPage,
      tabKey,
      Source: AnalyticsSourceValues.PROVIDER_DASHBOARD,
    });
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newSize: number) => {
    trackEvent?.(ProviderDashboardEvents.myPatientsItemsPerPageChanged, {
      itemsPerPage: newSize,
      tabKey,
      Source: AnalyticsSourceValues.PROVIDER_DASHBOARD,
    });
    setItemsPerPage(newSize);
    setCurrentPage(1);
  };

  return (
    <ChakraBox>
      {patients.length > 0 && (
        <>
          <ChakraFlex direction="column">
            {pageItems.map((patient) => (
              <RowComponent key={patient.patientExternalId} patient={patient} />
            ))}
          </ChakraFlex>
          <PaginationV2
            currentPage={currentPage}
            itemsPerPageOptions={[10, 25, 50]}
            onItemsPerPageChange={handleItemsPerPageChange}
            onPageChange={handlePageChange}
            shouldShowItemsPerPage={true}
            tableName={tableName}
            totalItems={patients.length}
          />
        </>
      )}
    </ChakraBox>
  );
};

export default PatientList;
